package com.smashvoter.screens

import androidx.compose.runtime.*
import com.smashvoter.SmashColors
import com.smashvoter.api.AdminPersonItem
import com.smashvoter.api.ApiClient
import com.smashvoter.api.RosterEntry
import com.smashvoter.api.TournamentConfig
import com.smashvoter.smashNavButton
import com.smashvoter.smashPrimaryButton
import com.smashvoter.state.AppState
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLStyleElement
import kotlin.math.roundToInt

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

private const val ADMIN_STYLE_ID = "admin-styles"

private val adminCss = """
@keyframes admin-spin {
    to { transform: rotate(360deg); }
}

.admin-tab {
    padding: 8px 20px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border: 1.5px solid transparent;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    outline: none;
    user-select: none;
}

.admin-tab-active {
    background: #E10D05;
    color: #fff;
    border-color: #E10D05;
}

.admin-tab-inactive {
    background: transparent;
    color: #8E8E93;
    border-color: #3A3A3C;
}

.admin-tab-inactive:hover {
    border-color: #E10D05;
    color: #fff;
}

.admin-person-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid #3A3A3C;
    background: #1C1C1E;
    margin-bottom: 8px;
    transition: border-color 0.15s;
}

.admin-person-row:hover {
    border-color: #555;
}

.admin-add-btn {
    padding: 5px 14px;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    border: 1.5px solid #E10D05;
    background: transparent;
    color: #E10D05;
    transition: background 0.15s, color 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
}

.admin-add-btn:hover:not(:disabled) {
    background: #E10D05;
    color: #fff;
}

.admin-add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.admin-remove-btn {
    padding: 4px 10px;
    border-radius: 14px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    border: 1.5px solid #555;
    background: transparent;
    color: #8E8E93;
    transition: border-color 0.15s, color 0.15s;
    white-space: nowrap;
    flex-shrink: 0;
}

.admin-remove-btn:hover {
    border-color: #F87171;
    color: #F87171;
}

.admin-input {
    width: 100%;
    background: #2C2C2E;
    border: 1.5px solid #3A3A3C;
    border-radius: 10px;
    padding: 10px 14px;
    color: #F5F5F7;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
}

.admin-input:focus {
    border-color: #E10D05;
}

.admin-select {
    width: 100%;
    background: #2C2C2E;
    border: 1.5px solid #3A3A3C;
    border-radius: 10px;
    padding: 10px 14px;
    color: #F5F5F7;
    font-size: 14px;
    outline: none;
    box-sizing: border-box;
    appearance: none;
    transition: border-color 0.15s;
    cursor: pointer;
}

.admin-select:focus {
    border-color: #E10D05;
}

.admin-section-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #8E8E93;
    margin-bottom: 12px;
}

.admin-elo-badge {
    font-size: 11px;
    font-weight: 700;
    color: #E10D05;
    background: rgba(225,13,5,0.12);
    padding: 2px 8px;
    border-radius: 10px;
    margin-left: 8px;
    white-space: nowrap;
}

.admin-success-card {
    background: rgba(52,211,153,0.1);
    border: 1.5px solid rgba(52,211,153,0.4);
    border-radius: 12px;
    padding: 24px;
    text-align: center;
}
""".trimIndent()

// ---------------------------------------------------------------------------
// Enums / Constants
// ---------------------------------------------------------------------------

private enum class AdminStep { STEP_NAMES, STEP1_BUILD_ROSTER, STEP2_ADD_NEW, STEP3_START, STEP4_CONFIG }

private const val ADMIN_EMAIL = "m.volkers@wearetriple.com"

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

@Composable
fun AdminScreen(apiClient: ApiClient) {
    DisposableEffect(Unit) {
        if (document.getElementById(ADMIN_STYLE_ID) == null) {
            val style = document.createElement("style") as HTMLStyleElement
            style.id = ADMIN_STYLE_ID
            style.textContent = adminCss
            document.head?.appendChild(style)
        }
        onDispose {}
    }

    val scope = rememberCoroutineScope()

    var currentStep by remember { mutableStateOf(AdminStep.STEP1_BUILD_ROSTER) }
    var isLoading by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }

    // Data
    var peopleList by remember { mutableStateOf<List<AdminPersonItem>>(emptyList()) }
    var onboardingList by remember { mutableStateOf<List<AdminPersonItem>>(emptyList()) }
    var roster by remember { mutableStateOf<List<RosterEntry>>(emptyList()) }
    var tournamentConfig by remember { mutableStateOf<TournamentConfig?>(null) }

    // Tournament started
    var tournamentStarted by remember { mutableStateOf(false) }
    var finalCount by remember { mutableStateOf(0) }

    // Load data — first verify server-side identity to prevent localStorage spoofing
    LaunchedEffect(Unit) {
        val sid = AppState.sessionId ?: run {
            AppState.clearSession()
            return@LaunchedEffect
        }
        try {
            // Always verify with the backend — never trust the locally-stored email alone.
            // If the session is invalid (401) or belongs to a different user, this throws
            // and we clear the session, which redirects to the login screen.
            val me = apiClient.getMe(sid)
            if (me.email.lowercase() != ADMIN_EMAIL) {
                AppState.clearSession()
                return@LaunchedEffect
            }

            val lists = apiClient.getAdminLists(sid)
            peopleList = lists.people
            onboardingList = lists.onboarding
            roster = apiClient.getRoster(sid)
            tournamentConfig = apiClient.getTournamentConfig(sid)
        } catch (_: Exception) {
            // Any error (401 invalid session, 403 wrong user, network issue) → force re-login
            AppState.clearSession()
        } finally {
            isLoading = false
        }
    }

    // Helper to refresh roster
    fun refreshRoster() {
        val sid = AppState.sessionId ?: return
        scope.launch {
            try {
                roster = apiClient.getRoster(sid)
            } catch (_: Exception) {
                errorMessage = "Failed to refresh roster"
            }
        }
    }

    // Helper to refresh source lists (people + onboarding) and their override names
    fun refreshLists() {
        val sid = AppState.sessionId ?: return
        scope.launch {
            try {
                val lists = apiClient.getAdminLists(sid)
                peopleList = lists.people
                onboardingList = lists.onboarding
            } catch (_: Exception) {
                errorMessage = "Failed to refresh lists"
            }
        }
    }

    // Page shell
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            minHeight(100.vh)
            backgroundColor(Color(SmashColors.background))
        }
    }) {
        // Nav bar
        Div(attrs = {
            style {
                backgroundColor(Color(SmashColors.surface))
                property("border-bottom", "1px solid ${SmashColors.border}")
                padding(14.px, 24.px)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                }
            }) {
                Img(src = "smash-logo.svg", attrs = {
                    style { width(36.px); height(36.px); property("flex-shrink", "0") }
                })
                Span(attrs = {
                    style {
                        color(Color(SmashColors.textPrimary))
                        fontSize(20.px)
                        fontWeight("900")
                        letterSpacing((-0.3).px)
                    }
                }) { Text("SmashVoter") }
                Span(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(13.px)
                        fontWeight("600")
                        marginLeft(8.px)
                        property("opacity", "0.7")
                    }
                }) { Text("Admin") }
            }
            Button(attrs = {
                onClick {
                    scope.launch {
                        val sid = AppState.sessionId
                        if (sid != null) try { apiClient.logout(sid) } catch (_: Exception) {}
                        AppState.clearSession()
                    }
                }
                style { smashNavButton() }
            }) { Text("Logout") }
        }

        // Content
        Div(attrs = {
            style {
                flex(1)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                padding(32.px, 24.px)
                property("box-sizing", "border-box")
            }
        }) {
            when {
                isLoading -> AdminSpinner()
                tournamentStarted -> TournamentStartedCard(count = finalCount)
                else -> {
                    // Step tabs
                    AdminStepTabs(
                        currentStep = currentStep,
                        onStepChange = { currentStep = it }
                    )

                    Div(attrs = { style { height(24.px) } }) {}

                    // Error banner
                    if (errorMessage != null) {
                        Div(attrs = {
                            style {
                                backgroundColor(Color("#3B1515"))
                                border(1.px, LineStyle.Solid, Color("#F87171"))
                                borderRadius(10.px)
                                padding(12.px, 16.px)
                                marginBottom(16.px)
                                color(Color("#F87171"))
                                fontSize(13.px)
                                maxWidth(900.px)
                                width(100.percent)
                                property("box-sizing", "border-box")
                            }
                        }) { Text(errorMessage!!) }
                    }

                    // Step content
                    when (currentStep) {
                        AdminStep.STEP_NAMES -> StepNamesContent(
                            apiClient = apiClient,
                            peopleList = peopleList,
                            onboardingList = onboardingList,
                            onListsRefreshed = { refreshLists() },
                            onError = { errorMessage = it }
                        )
                        AdminStep.STEP1_BUILD_ROSTER -> Step1Content(
                            apiClient = apiClient,
                            peopleList = peopleList,
                            onboardingList = onboardingList,
                            roster = roster,
                            onRosterChanged = { refreshRoster() },
                            onError = { errorMessage = it }
                        )
                        AdminStep.STEP2_ADD_NEW -> Step2Content(
                            apiClient = apiClient,
                            roster = roster,
                            onRosterChanged = { refreshRoster() },
                            onError = { errorMessage = it }
                        )
                        AdminStep.STEP3_START -> Step3Content(
                            apiClient = apiClient,
                            roster = roster,
                            onTournamentStarted = { count ->
                                finalCount = count
                                tournamentStarted = true
                            },
                            onError = { errorMessage = it }
                        )
                        AdminStep.STEP4_CONFIG -> Step4Content(
                            apiClient = apiClient,
                            initialConfig = tournamentConfig ?: TournamentConfig(),
                            roster = roster,
                            onConfigSaved = { saved -> tournamentConfig = saved },
                            onError = { errorMessage = it }
                        )
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Step tabs
// ---------------------------------------------------------------------------

@Composable
private fun AdminStepTabs(currentStep: AdminStep, onStepChange: (AdminStep) -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            flexWrap(FlexWrap.Wrap)
            justifyContent(JustifyContent.Center)
        }
    }) {
        listOf(
            AdminStep.STEP_NAMES to "Display Names",
            AdminStep.STEP1_BUILD_ROSTER to "1. Build Roster",
            AdminStep.STEP2_ADD_NEW to "2. Add New Person",
            AdminStep.STEP3_START to "3. Start Tournament",
            AdminStep.STEP4_CONFIG to "4. Tournament Config"
        ).forEach { (step, label) ->
            val isActive = currentStep == step
            Button(attrs = {
                classes("admin-tab", if (isActive) "admin-tab-active" else "admin-tab-inactive")
                onClick { onStepChange(step) }
            }) { Text(label) }
        }
    }
}

// ---------------------------------------------------------------------------
// Step 1 — Build roster from the 2 lists
// ---------------------------------------------------------------------------

@Composable
private fun Step1Content(
    apiClient: ApiClient,
    peopleList: List<AdminPersonItem>,
    onboardingList: List<AdminPersonItem>,
    roster: List<RosterEntry>,
    onRosterChanged: () -> Unit,
    onError: (String) -> Unit
) {
    val scope = rememberCoroutineScope()

    // Helper: is a source-person already in the roster?
    fun isInRoster(item: AdminPersonItem): Boolean {
        val effectiveName = item.overrideName?.takeIf { it.isNotBlank() } ?: item.name
        return roster.any { it.name == item.name || it.name == effectiveName }
    }

    fun addToRoster(item: AdminPersonItem) {
        val sid = AppState.sessionId ?: return
        val effectiveName = item.overrideName?.takeIf { it.isNotBlank() } ?: item.name
        scope.launch {
            try {
                apiClient.addToRoster(
                    name = effectiveName,
                    elo = item.elo,
                    sourceType = item.sourceType,
                    sourceId = item.sourceId,
                    sessionId = sid
                )
                onRosterChanged()
            } catch (e: Exception) {
                onError("Failed to add $effectiveName: ${e.message}")
            }
        }
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            gap(24.px)
            maxWidth(1000.px)
            width(100.percent)
            flexWrap(FlexWrap.Wrap)
            property("box-sizing", "border-box")
        }
    }) {
        // Left: source lists
        Div(attrs = {
            style {
                flex(1)
                minWidth(280.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(24.px)
            }
        }) {
            // People list
            AdminCard {
                P(attrs = { classes("admin-section-title") }) { Text("Participants (Voting List)") }
                if (peopleList.isEmpty()) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("No people found") }
                } else {
                    peopleList.forEach { item ->
                        val alreadyAdded = isInRoster(item)
                        PersonRow(item = item, alreadyAdded = alreadyAdded) {
                            addToRoster(item)
                        }
                    }
                }
            }

            // Onboarding list
            AdminCard {
                P(attrs = { classes("admin-section-title") }) { Text("Registered Players (Onboarding)") }
                if (onboardingList.isEmpty()) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("No onboarding entries found") }
                } else {
                    onboardingList.forEach { item ->
                        val alreadyAdded = isInRoster(item)
                        PersonRow(item = item, alreadyAdded = alreadyAdded) {
                            addToRoster(item)
                        }
                    }
                }
            }
        }

        // Right: current roster
        Div(attrs = {
            style {
                flex(1)
                minWidth(280.px)
            }
        }) {
            AdminCard {
                P(attrs = { classes("admin-section-title") }) {
                    Text("Tournament Roster (${roster.size})")
                }
                if (roster.isEmpty()) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("No one added yet") }
                } else {
                    roster.forEach { entry ->
                        RosterRow(entry = entry) {
                            val sid = AppState.sessionId ?: return@RosterRow
                            scope.launch {
                                try {
                                    apiClient.removeFromRoster(entry.id, sid)
                                    onRosterChanged()
                                } catch (_: Exception) {
                                    onError("Failed to remove ${entry.name}")
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Step 2 — Add a brand-new person between two roster members
// ---------------------------------------------------------------------------

@Composable
private fun Step2Content(
    apiClient: ApiClient,
    roster: List<RosterEntry>,
    onRosterChanged: () -> Unit,
    onError: (String) -> Unit
) {
    val scope = rememberCoroutineScope()

    var newName by remember { mutableStateOf("") }
    var selectedIndexA by remember { mutableStateOf(0) }
    var selectedIndexB by remember { mutableStateOf(if (roster.size > 1) 1 else 0) }
    var isSubmitting by remember { mutableStateOf(false) }
    var successMessage by remember { mutableStateOf<String?>(null) }

    val sortedRoster = remember(roster) { roster.sortedByDescending { it.elo } }
    val eloA = sortedRoster.getOrNull(selectedIndexA)?.elo ?: 0.0
    val eloB = sortedRoster.getOrNull(selectedIndexB)?.elo ?: 0.0
    val averageElo = if (roster.size >= 2 && selectedIndexA != selectedIndexB) {
        ((eloA + eloB) / 2.0 * 10).roundToInt() / 10.0
    } else null

    Div(attrs = {
        style {
            maxWidth(520.px)
            width(100.percent)
        }
    }) {
        AdminCard {
            P(attrs = { classes("admin-section-title") }) { Text("Add New Person") }

            if (roster.size < 2) {
                P(attrs = {
                    style { color(Color(SmashColors.textSecondary)); fontSize(14.px) }
                }) { Text("You need at least 2 people on the roster before adding a new person in between.") }
                return@AdminCard
            }

            // Name input
            AdminLabel("Name")
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(newName)
                attr("placeholder", "e.g. John Doe")
                onInput { newName = it.value }
                style { marginBottom(16.px) }
            })

            // Person A selector
            AdminLabel("Between person A")
            Select(attrs = {
                classes("admin-select")
                style { marginBottom(16.px) }
                onChange { selectedIndexA = it.value?.toIntOrNull() ?: 0 }
            }) {
                sortedRoster.forEachIndexed { idx, entry ->
                    Option(value = idx.toString(), attrs = {
                        if (idx == selectedIndexA) attr("selected", "selected")
                    }) { Text("${entry.name} (${entry.elo.toInt()})") }
                }
            }

            // Person B selector
            AdminLabel("And person B")
            Select(attrs = {
                classes("admin-select")
                style { marginBottom(20.px) }
                onChange { selectedIndexB = it.value?.toIntOrNull() ?: 0 }
            }) {
                sortedRoster.forEachIndexed { idx, entry ->
                    Option(value = idx.toString(), attrs = {
                        if (idx == selectedIndexB) attr("selected", "selected")
                    }) { Text("${entry.name} (${entry.elo.toInt()})") }
                }
            }

            // Calculated ELO
            if (averageElo != null) {
                Div(attrs = {
                    style {
                        backgroundColor(Color("#1C1C1E"))
                        border(1.px, LineStyle.Solid, Color("#3A3A3C"))
                        borderRadius(10.px)
                        padding(12.px, 16.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(20.px)
                    }
                }) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("Starting ELO") }
                    Span(attrs = {
                        style {
                            color(Color(SmashColors.accent))
                            fontSize(22.px)
                            fontWeight("900")
                        }
                    }) { Text(averageElo.toInt().toString()) }
                }
            } else if (selectedIndexA == selectedIndexB) {
                P(attrs = {
                    style { color(Color("#F87171")); fontSize(13.px); marginBottom(16.px) }
                }) { Text("Please select two different people.") }
            }

            // Success message
            if (successMessage != null) {
                P(attrs = {
                    style {
                        color(Color("#34D399"))
                        fontSize(13.px)
                        marginBottom(12.px)
                    }
                }) { Text(successMessage!!) }
            }

            // Submit
            Button(attrs = {
                if (isSubmitting || newName.isBlank() || averageElo == null) disabled()
                onClick {
                    if (newName.isBlank() || averageElo == null) return@onClick
                    val sid = AppState.sessionId ?: return@onClick
                    isSubmitting = true
                    successMessage = null
                    scope.launch {
                        try {
                            apiClient.addToRoster(
                                name = newName.trim(),
                                elo = averageElo,
                                sourceType = "manual",
                                sourceId = null,
                                sessionId = sid
                            )
                            onRosterChanged()
                            successMessage = "${newName.trim()} added with ELO $averageElo!"
                            newName = ""
                        } catch (e: Exception) {
                            onError("Failed to add person: ${e.message}")
                        } finally {
                            isSubmitting = false
                        }
                    }
                }
                style {
                    smashPrimaryButton()
                    if (isSubmitting || newName.isBlank() || averageElo == null) {
                        property("opacity", "0.5")
                    }
                }
            }) { Text(if (isSubmitting) "Adding…" else "Add to Roster") }
        }
    }
}

// ---------------------------------------------------------------------------
// Step 3 — Start the tournament
// ---------------------------------------------------------------------------

@Composable
private fun Step3Content(
    apiClient: ApiClient,
    roster: List<RosterEntry>,
    onTournamentStarted: (Int) -> Unit,
    onError: (String) -> Unit
) {
    val scope = rememberCoroutineScope()
    var isStarting by remember { mutableStateOf(false) }

    val sortedRoster = remember(roster) { roster.sortedByDescending { it.elo } }

    Div(attrs = {
        style {
            maxWidth(600.px)
            width(100.percent)
        }
    }) {
        AdminCard {
            P(attrs = { classes("admin-section-title") }) {
                Text("Final Roster — ${roster.size} participants")
            }

            if (roster.isEmpty()) {
                P(attrs = {
                    style { color(Color(SmashColors.textSecondary)); fontSize(14.px) }
                }) { Text("The roster is empty. Go back to Step 1 to add participants.") }
                return@AdminCard
            }

            // Roster summary (sorted by ELO desc)
            sortedRoster.forEachIndexed { idx, entry ->
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(8.px, 12.px)
                        borderRadius(8.px)
                        backgroundColor(Color(if (idx % 2 == 0) "#1C1C1E" else "transparent"))
                        marginBottom(4.px)
                    }
                }) {
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                        }
                    }) {
                        Span(attrs = {
                            style {
                                color(Color(SmashColors.textSecondary))
                                fontSize(12.px)
                                fontWeight("700")
                                minWidth(22.px)
                            }
                        }) { Text("#${idx + 1}") }
                        Span(attrs = {
                            style {
                                color(Color(SmashColors.textPrimary))
                                fontSize(14.px)
                            }
                        }) { Text(entry.name) }
                    }
                    Span(attrs = { classes("admin-elo-badge") }) { Text(entry.elo.toInt().toString()) }
                }
            }

            Div(attrs = { style { height(24.px) } }) {}

            // Warning
            P(attrs = {
                style {
                    color(Color(SmashColors.textSecondary))
                    fontSize(12.px)
                    marginBottom(16.px)
                    lineHeight("1.5")
                }
            }) {
                Text("Starting the tournament will commit the roster above to the final_roster table. This action can be repeated if needed — the previous final roster is replaced.")
            }

            Button(attrs = {
                if (isStarting) disabled()
                onClick {
                    val sid = AppState.sessionId ?: return@onClick
                    isStarting = true
                    scope.launch {
                        try {
                            val result = apiClient.startTournament(sid)
                            onTournamentStarted(result.participantCount)
                        } catch (e: Exception) {
                            onError("Failed to start tournament: ${e.message}")
                            isStarting = false
                        }
                    }
                }
                style {
                    smashPrimaryButton()
                    if (isStarting) property("opacity", "0.6")
                }
            }) { Text(if (isStarting) "Starting…" else "Start Tournament") }
        }
    }
}

// ---------------------------------------------------------------------------
// Step 4 — Tournament configuration
// ---------------------------------------------------------------------------

@Composable
private fun Step4Content(
    apiClient: ApiClient,
    initialConfig: TournamentConfig,
    roster: List<RosterEntry>,
    onConfigSaved: (TournamentConfig) -> Unit,
    onError: (String) -> Unit
) {
    val scope = rememberCoroutineScope()

    var numberOfSwitches by remember(initialConfig) { mutableStateOf(initialConfig.numberOfSwitches.toString()) }
    var numberOfPlayersPerMatch by remember(initialConfig) { mutableStateOf(initialConfig.numberOfPlayersPerMatch.toString()) }
    var rounds by remember(initialConfig) { mutableStateOf(initialConfig.rounds.toString()) }
    var podiumWaitTimeInSeconds by remember(initialConfig) { mutableStateOf(initialConfig.podiumWaitTimeInSeconds.toString()) }
    var startTime by remember { mutableStateOf("18:30") }
    var isSaving by remember { mutableStateOf(false) }
    var saveSuccess by remember { mutableStateOf(false) }

    // Derived values for the duration estimate
    val parsedSwitches = numberOfSwitches.toIntOrNull() ?: 0
    val parsedPlayersPerMatch = numberOfPlayersPerMatch.toIntOrNull() ?: 0
    val parsedRounds = rounds.toIntOrNull() ?: 0
    val parsedPodiumSecs = podiumWaitTimeInSeconds.toIntOrNull() ?: 0

    // Scheduler math (mirrors Parameters.kt in the scheduler module):
    //   courts  = ceil(players / playersPerMatch)   → total courts per round
    //   cycles  = ceil(courts / numberOfSwitches)   → batches of courts per round
    // Each cycle = 10 min (7 min play + 3 min switching)
    // Total time = rounds × cycles × 10 min + podiumWait
    val matchDurationMinutes = 10
    val matchPlayMinutes = 7
    val matchSwitchMinutes = 3

    val playerCount = roster.size
    val numberOfCourts = if (parsedPlayersPerMatch > 0 && playerCount > 0)
        kotlin.math.ceil(playerCount.toDouble() / parsedPlayersPerMatch).toInt()
    else null
    val cyclesPerRound = if (numberOfCourts != null && parsedSwitches > 0)
        kotlin.math.ceil(numberOfCourts.toDouble() / parsedSwitches).toInt()
    else null

    val totalMatchMinutes = if (cyclesPerRound != null) parsedRounds * cyclesPerRound * matchDurationMinutes else null
    val podiumMinutes = parsedPodiumSecs / 60
    val totalMinutes = if (totalMatchMinutes != null) totalMatchMinutes + podiumMinutes else null
    val totalHours = totalMinutes?.div(60)
    val remainingMinutes = totalMinutes?.rem(60)

    // Compute end time from start time + totalMinutes
    val endTime: String? = totalMinutes?.let { total ->
        val parts = startTime.split(":")
        val startH = parts.getOrNull(0)?.toIntOrNull()
        val startM = parts.getOrNull(1)?.toIntOrNull()
        if (startH != null && startM != null) {
            val endTotalMins = startH * 60 + startM + total
            val endH = (endTotalMins / 60) % 24
            val endM = endTotalMins % 60
            "${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}"
        } else null
    }

    fun buildConfig(): TournamentConfig? {
        val s = numberOfSwitches.toIntOrNull() ?: return null
        val p = numberOfPlayersPerMatch.toIntOrNull() ?: return null
        val r = rounds.toIntOrNull() ?: return null
        val pod = podiumWaitTimeInSeconds.toIntOrNull() ?: return null
        if (s < 1 || p < 1 || r < 1 || pod < 0) return null
        return TournamentConfig(s, p, r, pod)
    }

    val isValid = buildConfig() != null

    Div(attrs = {
        style {
            maxWidth(580.px)
            width(100.percent)
        }
    }) {
        AdminCard {
            P(attrs = { classes("admin-section-title") }) { Text("Tournament Format") }

            // --- Inputs ---
            AdminLabel("Simultaneous Matches (numberOfSwitches)")
            Div(attrs = {
                style {
                    color(Color(SmashColors.textSecondary))
                    fontSize(11.px)
                    marginBottom(6.px)
                    lineHeight("1.4")
                }
            }) { Text("How many courts/matches can be played at the same time per cycle") }
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(numberOfSwitches)
                attr("inputmode", "numeric")
                attr("placeholder", "e.g. 3")
                onInput { numberOfSwitches = it.value; saveSuccess = false }
                style { marginBottom(16.px) }
            })

            AdminLabel("Number of Players per Match")
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(numberOfPlayersPerMatch)
                attr("inputmode", "numeric")
                attr("placeholder", "e.g. 4")
                onInput { numberOfPlayersPerMatch = it.value; saveSuccess = false }
                style { marginBottom(16.px) }
            })

            AdminLabel("Number of Rounds")
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(rounds)
                attr("inputmode", "numeric")
                attr("placeholder", "e.g. 10")
                onInput { rounds = it.value; saveSuccess = false }
                style { marginBottom(16.px) }
            })

            AdminLabel("Podium Wait Time (seconds)")
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(podiumWaitTimeInSeconds)
                attr("inputmode", "numeric")
                attr("placeholder", "e.g. 300")
                onInput { podiumWaitTimeInSeconds = it.value; saveSuccess = false }
                style { marginBottom(16.px) }
            })

            AdminLabel("Tournament Start Time")
            Input(type = org.jetbrains.compose.web.attributes.InputType.Time, attrs = {
                classes("admin-input")
                value(startTime)
                onInput { startTime = it.value }
                style { marginBottom(24.px) }
            })

            // --- Duration estimate card ---
            if (isValid && parsedRounds > 0) {
                Div(attrs = {
                    style {
                        backgroundColor(Color("#0D1F0D"))
                        border(1.px, LineStyle.Solid, Color("#2A4A2A"))
                        borderRadius(12.px)
                        padding(18.px, 20.px)
                        marginBottom(20.px)
                    }
                }) {
                    P(attrs = {
                        classes("admin-section-title")
                        style { color(Color("#4ADE80")); marginBottom(12.px) }
                    }) { Text("Estimated Duration") }

                    // Assumption note
                    Div(attrs = {
                        style {
                            color(Color("#86EFAC"))
                            fontSize(12.px)
                            marginBottom(12.px)
                            lineHeight("1.5")
                        }
                    }) {
                        Text("Assuming $matchPlayMinutes min play + $matchSwitchMinutes min switching = $matchDurationMinutes min per cycle")
                    }

                    // Structural breakdown
                    if (playerCount > 0 && numberOfCourts != null) {
                        DurationRow("Players in roster", "$playerCount")
                        DurationRow("Courts per round", "$numberOfCourts (ceil($playerCount ÷ $parsedPlayersPerMatch))")
                    }
                    if (cyclesPerRound != null) {
                        DurationRow("Cycles per round", "$cyclesPerRound (ceil(${numberOfCourts} ÷ $parsedSwitches) — $parsedSwitches courts at a time)")
                        DurationRow("Rounds", "$parsedRounds")
                        if (totalMatchMinutes != null) {
                            DurationRow("Match time", "$parsedRounds × $cyclesPerRound × $matchDurationMinutes min = $totalMatchMinutes min")
                        }
                    } else {
                        // No roster yet — show formula-only estimate
                        DurationRow("Rounds", "$parsedRounds")
                    }
                    if (parsedPodiumSecs > 0) {
                        DurationRow("Podium wait", "$podiumMinutes min")
                    }

                    // Total + start/end (only shown when full calculation is possible)
                    if (totalMinutes != null) {
                        Div(attrs = {
                            style {
                                height(1.px)
                                backgroundColor(Color("#2A4A2A"))
                                marginTop(10.px)
                                marginBottom(14.px)
                            }
                        }) {}

                        // Duration row
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                marginBottom(14.px)
                            }
                        }) {
                            Span(attrs = {
                                style {
                                    color(Color("#4ADE80"))
                                    fontSize(13.px)
                                    fontWeight("700")
                                }
                            }) { Text("Total duration") }
                            Span(attrs = {
                                style {
                                    color(Color("#4ADE80"))
                                    fontSize(20.px)
                                    fontWeight("900")
                                }
                            }) {
                                Text(
                                    if ((totalHours ?: 0) > 0) "${totalHours}h ${remainingMinutes}m"
                                    else "${totalMinutes}m"
                                )
                            }
                        }

                        // Start → End time
                        if (endTime != null) {
                            Div(attrs = {
                                style {
                                    backgroundColor(Color("#0A2A0A"))
                                    border(1.px, LineStyle.Solid, Color("#3A6A3A"))
                                    borderRadius(10.px)
                                    padding(12.px, 16.px)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    justifyContent(JustifyContent.SpaceBetween)
                                }
                            }) {
                                // Start
                                Div(attrs = { style { textAlign("center") } }) {
                                    Div(attrs = {
                                        style {
                                            color(Color("#86EFAC"))
                                            fontSize(10.px)
                                            fontWeight("700")
                                            letterSpacing(0.8.px)
                                            property("text-transform", "uppercase")
                                            marginBottom(2.px)
                                        }
                                    }) { Text("Start") }
                                    Div(attrs = {
                                        style {
                                            color(Color("#D1FAE5"))
                                            fontSize(24.px)
                                            fontWeight("900")
                                        }
                                    }) { Text(startTime) }
                                }

                                // Arrow
                                Span(attrs = {
                                    style {
                                        color(Color("#4ADE80"))
                                        fontSize(22.px)
                                        fontWeight("300")
                                        property("flex-shrink", "0")
                                    }
                                }) { Text("→") }

                                // End
                                Div(attrs = { style { textAlign("center") } }) {
                                    Div(attrs = {
                                        style {
                                            color(Color("#86EFAC"))
                                            fontSize(10.px)
                                            fontWeight("700")
                                            letterSpacing(0.8.px)
                                            property("text-transform", "uppercase")
                                            marginBottom(2.px)
                                        }
                                    }) { Text("End") }
                                    Div(attrs = {
                                        style {
                                            color(Color("#4ADE80"))
                                            fontSize(24.px)
                                            fontWeight("900")
                                        }
                                    }) { Text(endTime) }
                                }
                            }
                        }
                    } else {
                        // No roster loaded yet
                        Div(attrs = {
                            style {
                                color(Color("#86EFAC"))
                                fontSize(12.px)
                                marginTop(8.px)
                                fontStyle("italic")
                            }
                        }) {
                            Text("Add players to the roster to see a full time estimate")
                        }
                    }
                }
            }

            // Success message
            if (saveSuccess) {
                Div(attrs = {
                    style {
                        color(Color("#34D399"))
                        fontSize(13.px)
                        marginBottom(12.px)
                    }
                }) { Text("Configuration saved!") }
            }

            // Save button
            Button(attrs = {
                if (isSaving || !isValid) disabled()
                onClick {
                    val config = buildConfig() ?: return@onClick
                    val sid = AppState.sessionId ?: return@onClick
                    isSaving = true
                    saveSuccess = false
                    scope.launch {
                        try {
                            val saved = apiClient.saveTournamentConfig(config, sid)
                            onConfigSaved(saved)
                            saveSuccess = true
                        } catch (e: Exception) {
                            onError("Failed to save config: ${e.message}")
                        } finally {
                            isSaving = false
                        }
                    }
                }
                style {
                    smashPrimaryButton()
                    if (isSaving || !isValid) property("opacity", "0.5")
                }
            }) { Text(if (isSaving) "Saving…" else "Save Configuration") }
        }
    }
}

@Composable
private fun DurationRow(label: String, value: String) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            marginBottom(6.px)
        }
    }) {
        Span(attrs = {
            style { color(Color("#86EFAC")); fontSize(13.px) }
        }) { Text(label) }
        Span(attrs = {
            style { color(Color("#D1FAE5")); fontSize(13.px); fontWeight("600") }
        }) { Text(value) }
    }
}

// ---------------------------------------------------------------------------
// Success state
// ---------------------------------------------------------------------------

@Composable
private fun TournamentStartedCard(count: Int) {
    Div(attrs = {
        style {
            maxWidth(440.px)
            width(100.percent)
            textAlign("center")
        }
    }) {
        Div(attrs = { style { fontSize(64.px); marginBottom(16.px) } }) { Text("🏆") }

        H1(attrs = {
            style {
                color(Color(SmashColors.textPrimary))
                fontSize(26.px)
                fontWeight("900")
                marginTop(0.px)
                marginBottom(8.px)
            }
        }) { Text("Tournament started!") }

        P(attrs = {
            style {
                color(Color(SmashColors.textSecondary))
                fontSize(15.px)
                lineHeight("1.6")
                marginTop(0.px)
                marginBottom(24.px)
            }
        }) { Text("$count participants have been committed to the final roster. Let the Smashing begin!") }
    }
}

// ---------------------------------------------------------------------------
// Display Names step — set per-player name overrides
// ---------------------------------------------------------------------------

@Composable
private fun StepNamesContent(
    apiClient: ApiClient,
    peopleList: List<AdminPersonItem>,
    onboardingList: List<AdminPersonItem>,
    onListsRefreshed: () -> Unit,
    onError: (String) -> Unit
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            gap(24.px)
            maxWidth(1000.px)
            width(100.percent)
            flexWrap(FlexWrap.Wrap)
            property("box-sizing", "border-box")
        }
    }) {
        // Left: People list overrides
        Div(attrs = {
            style {
                flex(1)
                minWidth(300.px)
            }
        }) {
            AdminCard {
                P(attrs = { classes("admin-section-title") }) { Text("Participants (Voting List)") }
                Div(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(12.px)
                        marginBottom(16.px)
                        lineHeight("1.5")
                    }
                }) {
                    Text("Set a display name that will be used in the roster instead of the original name. Leave blank to use the original name.")
                }
                if (peopleList.isEmpty()) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("No people found") }
                } else {
                    peopleList.forEach { item ->
                        OverrideRow(
                            item = item,
                            apiClient = apiClient,
                            onSaved = { onListsRefreshed() },
                            onError = onError
                        )
                    }
                }
            }
        }

        // Right: Onboarding list overrides
        Div(attrs = {
            style {
                flex(1)
                minWidth(300.px)
            }
        }) {
            AdminCard {
                P(attrs = { classes("admin-section-title") }) { Text("Registered Players (Onboarding)") }
                Div(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(12.px)
                        marginBottom(16.px)
                        lineHeight("1.5")
                    }
                }) {
                    Text("Override a player's nickname with a different display name for the roster.")
                }
                if (onboardingList.isEmpty()) {
                    Span(attrs = {
                        style { color(Color(SmashColors.textSecondary)); fontSize(13.px) }
                    }) { Text("No onboarding entries found") }
                } else {
                    onboardingList.forEach { item ->
                        OverrideRow(
                            item = item,
                            apiClient = apiClient,
                            onSaved = { onListsRefreshed() },
                            onError = onError
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun OverrideRow(
    item: AdminPersonItem,
    apiClient: ApiClient,
    onSaved: () -> Unit,
    onError: (String) -> Unit
) {
    var inputValue by remember(item.id, item.overrideName) {
        mutableStateOf(item.overrideName ?: "")
    }
    var isSaving by remember { mutableStateOf(false) }
    var showSuccess by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    val hasUnsavedChange = inputValue != (item.overrideName ?: "")

    Div(attrs = {
        style {
            borderRadius(10.px)
            border(1.px, LineStyle.Solid, Color(SmashColors.border))
            backgroundColor(Color("#1C1C1E"))
            padding(12.px, 14.px)
            marginBottom(8.px)
        }
    }) {
        // Original name row
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(8.px)
            }
        }) {
            Span(attrs = {
                style {
                    color(Color(SmashColors.textPrimary))
                    fontSize(14.px)
                    fontWeight("600")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                    flex(1)
                    marginRight(8.px)
                }
            }) { Text(item.name) }
            Span(attrs = { classes("admin-elo-badge") }) { Text(item.elo.toInt().toString()) }
        }

        // Input row
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            Input(type = org.jetbrains.compose.web.attributes.InputType.Text, attrs = {
                classes("admin-input")
                value(inputValue)
                attr("placeholder", "Override name (leave blank to remove)…")
                onInput { inputValue = it.value; showSuccess = false }
                style {
                    flex(1)
                    fontSize(13.px)
                    padding(7.px, 12.px)
                    marginBottom(0.px)
                }
            })
            Button(attrs = {
                classes("admin-add-btn")
                if (isSaving || !hasUnsavedChange) disabled()
                onClick {
                    if (!hasUnsavedChange) return@onClick
                    val sid = AppState.sessionId ?: return@onClick
                    isSaving = true
                    showSuccess = false
                    scope.launch {
                        try {
                            apiClient.setNameOverride(
                                sourceType = item.sourceType,
                                sourceId = item.sourceId,
                                overrideName = inputValue.trim(),
                                sessionId = sid
                            )
                            showSuccess = true
                            onSaved()
                        } catch (e: Exception) {
                            onError("Failed to save override for ${item.name}: ${e.message}")
                        } finally {
                            isSaving = false
                        }
                    }
                }
                style {
                    property("flex-shrink", "0")
                    if (isSaving || !hasUnsavedChange) property("opacity", "0.4")
                }
            }) { Text(if (isSaving) "…" else "Save") }
        }

        // Success indicator
        if (showSuccess) {
            Div(attrs = {
                style {
                    color(Color("#34D399"))
                    fontSize(11.px)
                    marginTop(6.px)
                }
            }) {
                Text(if (inputValue.isBlank()) "Override removed" else "Saved: \"${inputValue.trim()}\"")
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

@Composable
private fun AdminCard(content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            backgroundColor(Color(SmashColors.surface))
            border(1.px, LineStyle.Solid, Color(SmashColors.border))
            borderRadius(14.px)
            padding(24.px)
            property("box-shadow", "0 4px 16px rgba(0,0,0,0.4)")
            width(100.percent)
            property("box-sizing", "border-box")
        }
    }) { content() }
}

@Composable
private fun AdminLabel(text: String) {
    Div(attrs = {
        style {
            color(Color(SmashColors.textSecondary))
            fontSize(11.px)
            fontWeight("700")
            letterSpacing(0.6.px)
            property("text-transform", "uppercase")
            marginBottom(6.px)
        }
    }) { Text(text) }
}

@Composable
private fun PersonRow(item: AdminPersonItem, alreadyAdded: Boolean, onAdd: () -> Unit) {
    val effectiveName = item.overrideName?.takeIf { it.isNotBlank() } ?: item.name
    Div(attrs = { classes("admin-person-row") }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(2.px)
                minWidth(0.px)
                flex(1)
            }
        }) {
            Span(attrs = {
                style {
                    color(Color(SmashColors.textPrimary))
                    fontSize(14.px)
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(effectiveName) }
            if (item.overrideName != null) {
                Span(attrs = {
                    style {
                        color(Color(SmashColors.textSecondary))
                        fontSize(11.px)
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    }
                }) { Text("(original: ${item.name})") }
            }
        }
        Span(attrs = { classes("admin-elo-badge") }) { Text(item.elo.toInt().toString()) }
        Button(attrs = {
            classes("admin-add-btn")
            if (alreadyAdded) disabled()
            onClick { if (!alreadyAdded) onAdd() }
        }) { Text(if (alreadyAdded) "Added" else "Add") }
    }
}

@Composable
private fun RosterRow(entry: RosterEntry, onRemove: () -> Unit) {
    Div(attrs = { classes("admin-person-row") }) {
        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                flex(1)
                minWidth(0.px)
            }
        }) {
            Span(attrs = {
                style {
                    color(Color(SmashColors.textPrimary))
                    fontSize(14.px)
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    property("white-space", "nowrap")
                }
            }) { Text(entry.name) }
            Span(attrs = { classes("admin-elo-badge") }) { Text(entry.elo.toInt().toString()) }
        }
        Button(attrs = {
            classes("admin-remove-btn")
            onClick { onRemove() }
        }) { Text("Remove") }
    }
}

@Composable
private fun AdminSpinner() {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(12.px)
            marginTop(60.px)
        }
    }) {
        Div(attrs = {
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                border(3.px, LineStyle.Solid, Color(SmashColors.border))
                property("border-top-color", SmashColors.accent)
                property("animation", "admin-spin 0.75s linear infinite")
            }
        }) {}
        Span(attrs = {
            style { color(Color(SmashColors.textSecondary)); fontSize(14.px) }
        }) { Text("Loading…") }
    }
}
